import { Box, Popper } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  type KeyboardEvent,
  type MouseEventHandler,
  memo,
  type ReactNode,
  type Ref,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { HoverMenuRoot, hoverMenuCardBaseSx } from './hover_menu_surface.tsx';

/** Matches the `hover_menu_surface` exit transition. */
const EXIT_MS = 240;

function getPopperSx(interactive: boolean): SxProps<Theme> {
  return {
    zIndex: 'calc(var(--mui-zIndex-appBar) + 1)',
    // Closed (keepMounted) poppers must not steal hits from an open sibling menu.
    pointerEvents: interactive ? 'auto' : 'none',
  };
}

/** Menu items are focusable only while the menu is open. */
export function hoverMenuItemTabIndex(open: boolean): 0 | -1 {
  return open ? 0 : -1;
}

export interface HoverMenuProps {
  id: string;
  open: boolean;
  /** False until first open so the content isn't mounted on page load. */
  mounted: boolean;
  anchorEl: HTMLElement | null;
  children: ReactNode;
  originX?: 'left' | 'right' | 'center';
  width?: number | string;
  cardSx?: SxProps<Theme>;
  rootRef?: Ref<HTMLDivElement>;
  onEscape?: () => void;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
}

/** Popper surface for header menus (Fund Account, Wallet). Pair with `useHoverMenu`. */
export const HoverMenu = memo(function HoverMenu({
  id,
  open,
  mounted,
  anchorEl,
  children,
  originX = 'center',
  width,
  cardSx,
  rootRef,
  onEscape,
  onMouseEnter,
  onMouseLeave,
}: HoverMenuProps) {
  // `presented` keeps the Popper up through the exit transition; `visuallyOpen` drives the CSS.
  const [presented, setPresented] = useState(false);
  const [visuallyOpen, setVisuallyOpen] = useState(false);

  useLayoutEffect(() => {
    if (!mounted) {
      setPresented(false);
      setVisuallyOpen(false);
      return;
    }

    if (open) {
      setPresented(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisuallyOpen(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    setVisuallyOpen(false);
    const timeout = window.setTimeout(() => setPresented(false), EXIT_MS);
    return () => window.clearTimeout(timeout);
  }, [mounted, open]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape?.();
      }
    },
    [onEscape]
  );

  if (!mounted) return null;

  return (
    <Popper
      id={id}
      open={presented}
      keepMounted
      anchorEl={anchorEl}
      placement="bottom"
      sx={getPopperSx(open || visuallyOpen)}
      modifiers={[
        { name: 'flip', enabled: true },
        { name: 'preventOverflow', options: { padding: 16 } },
      ]}
    >
      <HoverMenuRoot
        ref={rootRef}
        data-open={visuallyOpen}
        originX={originX}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={handleKeyDown}
        aria-hidden={!open}
        sx={{ width, maxWidth: 'calc(100vw - 32px)' }}
      >
        <Box data-menu-card sx={[hoverMenuCardBaseSx, ...(Array.isArray(cardSx) ? cardSx : cardSx ? [cardSx] : [])]}>
          {children}
        </Box>
      </HoverMenuRoot>
    </Popper>
  );
});
